import React, { useState } from 'react';
import { useOrganizations } from '@/hooks/useOrganizations';
import { Button } from '@/components/ui/button';
import { Pencil } from 'lucide-react';
import { EnhancedOrganizationForm } from '@/components/organization/EnhancedOrganizationForm';
import { useToast } from '@/hooks/use-toast';
import { OrganizationWithRelations, ServiceDetails } from '@/types/organization.types';
import { BasicInfoCard } from '@/components/organization/details/BasicInfoCard';
import { ContractInfoCard } from '@/components/organization/details/ContractInfoCard';
import { SocialMediaCard } from '@/components/organization/details/SocialMediaCard';
import { ClientPreferencesCard } from '@/components/organization/details/ClientPreferencesCard';
import { OrganizationStructureCard } from '@/components/organization/details/OrganizationStructureCard';
import { ClientGoalsCard } from '@/components/organization/details/ClientGoalsCard';
import { RelationshipHealthSummary } from '@/components/organization/RelationshipHealthSummary';
import { ServiceDetailsCard } from '@/components/organization/details/ServiceDetailsCard';
import { AdditionalDetailsCard } from '@/components/organization/details/AdditionalDetailsCard';
import { AccountHealthCard } from '@/components/organization/AccountHealthCard';
import { ServiceManagementDialog } from '@/components/organization/ServiceManagementDialog';

export interface DetailsTabProps {
  organization: OrganizationWithRelations;
  organizationId: string;
  contacts: any[];
}

export function DetailsTab({ organization, organizationId, contacts }: DetailsTabProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<ServiceDetails | null>(null);
  const { updateOrganization } = useOrganizations();
  const { toast } = useToast();

  const handleUpdate = async (data: any) => {
    try {
      await updateOrganization({
        id: organizationId,
        data: {
          ...data,
          start_date: data.start_date ? data.start_date.toISOString() : null,
          contract_renewal_date: data.contract_renewal_date
            ? data.contract_renewal_date.toISOString()
            : null,
        },
      });
      toast({
        title: 'Organization updated',
        description: 'The organization details have been successfully updated.',
      });
      setIsEditing(false);
    } catch (error) {
      toast({
        title: 'Update failed',
        description: 'There was an error updating the organization.',
        variant: 'destructive',
      });
    }
  };

  const handleServiceSave = async (serviceData: Partial<ServiceDetails>) => {
    try {
      const currentServices = organization.services || [];
      let updatedServices;

      if (editingService) {
        // Update existing service
        updatedServices = currentServices.map((service) =>
          service.id === editingService.id ? { ...service, ...serviceData } : service
        );
      } else {
        // Add new service
        const newService: ServiceDetails = {
          id: crypto.randomUUID(),
          ...serviceData,
        } as ServiceDetails;
        updatedServices = [...currentServices, newService];
      }

      await updateOrganization({
        id: organizationId,
        data: {
          services: updatedServices,
        },
      });

      toast({
        title: editingService ? 'Service updated' : 'Service added',
        description: `${serviceData.name} has been ${editingService ? 'updated' : 'added'} successfully.`,
      });

      setServiceDialogOpen(false);
      setEditingService(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${editingService ? 'update' : 'add'} service.`,
        variant: 'destructive',
      });
    }
  };

  const handleAddService = () => {
    setEditingService(null);
    setServiceDialogOpen(true);
  };

  const handleEditService = (service: ServiceDetails) => {
    setEditingService(service);
    setServiceDialogOpen(true);
  };

  if (isEditing) {
    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit Organization Details</h2>
          <Button variant="outline" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
        </div>
        <EnhancedOrganizationForm
          organization={organization}
          onSubmit={handleUpdate}
          contacts={contacts}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Organization Details</h2>
        <Button variant="outline" onClick={() => setIsEditing(true)}>
          <Pencil className="h-4 w-4 mr-2" />
          Edit
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <BasicInfoCard organization={organization} />
        <ServiceDetailsCard
          services={organization.services || []}
          isEditable={true}
          onAdd={handleAddService}
          onEdit={handleEditService}
        />
        <AccountHealthCard organizationId={organizationId} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ContractInfoCard organization={organization} />
      </div>

      <RelationshipHealthSummary organizationId={organizationId} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SocialMediaCard organization={organization} />
        <ClientPreferencesCard organization={organization} />
      </div>

      <OrganizationStructureCard organization={organization} />
      <ClientGoalsCard organization={organization} />
      <AdditionalDetailsCard organization={organization} contacts={contacts} />

      <ServiceManagementDialog
        open={serviceDialogOpen}
        onOpenChange={setServiceDialogOpen}
        service={editingService}
        onSave={handleServiceSave}
      />
    </div>
  );
}
